import { Checkbox } from '@/common/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { Label } from '@/common/ui/label'
import { MessageChannel } from '../../types'
import { useState } from 'react'

interface ChannelSelectorProps {
	value: MessageChannel[]
	onChange: (channels: MessageChannel[]) => void
	disabled?: boolean
}

type MessageMode = 'text' | 'hot_games' | 'promotion' | null

export const ChannelSelector = ({
	value,
	onChange,
	disabled = false,
}: ChannelSelectorProps) => {
	// Determine current mode based on selected channels
	const [mode, setMode] = useState<MessageMode>(() => {
		if (value.includes(MessageChannel.LINE_HOT_GAME)) return 'hot_games'
		if (value.includes(MessageChannel.LINE_PROMOTION)) return 'promotion'
		if (
			value.includes(MessageChannel.SMS) ||
			value.includes(MessageChannel.LINE)
		)
			return 'text'
		return null
	})

	const handleModeChange = (newMode: string) => {
		setMode(newMode as MessageMode)
		if (newMode === 'hot_games') {
			onChange([MessageChannel.LINE_HOT_GAME])
		} else if (newMode === 'promotion') {
			onChange([MessageChannel.LINE_PROMOTION])
		} else if (newMode === 'text') {
			onChange([]) // Clear selection, user must choose SMS/LINE
		}
	}

	const handleTextChannelToggle = (
		channel: MessageChannel.SMS | MessageChannel.LINE,
		checked: boolean,
	) => {
		if (checked) {
			// Add channel
			if (!value.includes(channel)) {
				onChange([...value, channel])
			}
		} else {
			// Remove channel
			const newChannels = value.filter((c) => c !== channel)
			onChange(newChannels)
		}
	}

	return (
		<div className="space-y-4">
			<div>
				<Label className="text-base">ประเภทข้อความ</Label>
				<p className="text-sm text-muted-foreground">
					เลือกประเภทข้อความที่ต้องการส่ง
				</p>
			</div>

			{/* Mode Selection: Text or Hot Games */}
			<RadioGroup
				value={mode || ''}
				onValueChange={handleModeChange}
				disabled={disabled}
			>
				<div className="space-y-3">
					<div className="flex items-center space-x-3">
						<RadioGroupItem value="text" id="mode-text" />
						<Label
							htmlFor="mode-text"
							className="cursor-pointer text-sm font-normal"
						>
							ข้อความธรรมดา (SMS/LINE)
						</Label>
					</div>
					<div className="flex items-center space-x-3">
						<RadioGroupItem value="hot_games" id="mode-hot-games" />
						<Label
							htmlFor="mode-hot-games"
							className="cursor-pointer text-sm font-normal"
						>
							LINE Hot Games
						</Label>
					</div>
					<div className="flex items-center space-x-3">
						<RadioGroupItem value="promotion" id="mode-promotion" />
						<Label
							htmlFor="mode-promotion"
							className="cursor-pointer text-sm font-normal"
						>
							LINE Promotion Card
						</Label>
					</div>
				</div>
			</RadioGroup>

			{/* Text Channels - SMS & LINE */}
			{mode === 'text' && (
				<div className="ml-6 space-y-3 rounded-md border p-4">
					<div className="text-sm font-medium">เลือกช่องทางการส่ง</div>
					<div className="space-y-2">
						<div className="flex items-center space-x-3">
							<Checkbox
								id="channel-sms"
								checked={value.includes(MessageChannel.SMS)}
								onCheckedChange={(checked) =>
									handleTextChannelToggle(
										MessageChannel.SMS,
										checked as boolean,
									)
								}
								disabled={disabled}
							/>
							<Label
								htmlFor="channel-sms"
								className="cursor-pointer text-sm font-normal"
							>
								SMS
							</Label>
						</div>
						<div className="flex items-center space-x-3">
							<Checkbox
								id="channel-line"
								checked={value.includes(MessageChannel.LINE)}
								onCheckedChange={(checked) =>
									handleTextChannelToggle(
										MessageChannel.LINE,
										checked as boolean,
									)
								}
								disabled={disabled}
							/>
							<Label
								htmlFor="channel-line"
								className="cursor-pointer text-sm font-normal"
							>
								LINE
							</Label>
						</div>
					</div>
					{value.length === 0 && (
						<p className="text-xs text-muted-foreground">
							กรุณาเลือกอย่างน้อย 1 ช่องทาง
						</p>
					)}
				</div>
			)}

			{/* Hot Games - No additional selection needed */}
			{mode === 'hot_games' && (
				<div className="ml-6 rounded-md border border-green-200 bg-green-50 p-4">
					<p className="text-sm text-green-900">
						ข้อความจะถูกส่งผ่าน LINE Hot Games
					</p>
				</div>
			)}

			{/* Promotion Card - No additional selection needed */}
			{mode === 'promotion' && (
				<div className="ml-6 rounded-md border border-purple-200 bg-purple-50 p-4">
					<p className="text-sm text-purple-900">
						ข้อความจะถูกส่งผ่าน LINE Promotion Card
					</p>
				</div>
			)}

			{/* No mode selected */}
			{!mode && (
				<p className="text-sm text-muted-foreground">กรุณาเลือกประเภทข้อความ</p>
			)}
		</div>
	)
}
